import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CATEGORIES, MAX_ROUNDS, MIN_ROUNDS } from '../game';
import { playerDisplayName } from '../game/presentation';
import { humanizeFirebaseError, useGame } from '../hooks/useGame';
import { shareGame } from '../utils/share';
import MuteButton from './play/MuteButton';
import { ScreenError, ScreenLoading } from './play/ScreenState';

const GameLobby: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { game, loading, error, dispatch, setError } = useGame(gameId);
  const [copied, setCopied] = useState(false);
  const [starting, setStarting] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);

  const me = useMemo(
    () => game?.players.find((player) => player.id === currentUser?.uid),
    [game, currentUser]
  );
  const isHost = Boolean(me?.isHost);

  useEffect(() => {
    if (!game || !currentUser) {
      return;
    }
    if (game.status !== 'lobby') {
      navigate(`/game-board/${game.id}`, { replace: true });
      return;
    }
    if (!game.playerIds.includes(currentUser.uid)) {
      dispatch({
        type: 'JOIN',
        playerId: currentUser.uid,
        playerName: playerDisplayName(currentUser),
      }).catch((err) => setError(humanizeFirebaseError(err)));
    }
  }, [game, currentUser, dispatch, navigate, setError]);

  useEffect(() => {
    if (!game || !currentUser || !isHost || game.status !== 'lobby') {
      return;
    }
    if (game.selectedCategoryIds.length === game.totalRounds) {
      return;
    }
    const selectedCategoryIds = CATEGORIES.slice(0, game.totalRounds).map((category) => category.id);
    dispatch({
      type: 'SET_CONFIG',
      playerId: currentUser.uid,
      totalRounds: game.totalRounds,
      selectedCategoryIds,
    }).catch((err) => setError(humanizeFirebaseError(err)));
  }, [game, currentUser, isHost, dispatch, setError]);

  const handleNumRoundsChange = async (totalRounds: number) => {
    if (!currentUser || !game) return;
    const selectedCategoryIds = [...game.selectedCategoryIds];
    while (selectedCategoryIds.length < totalRounds) {
      const nextCategory = CATEGORIES.find((category) => !selectedCategoryIds.includes(category.id));
      selectedCategoryIds.push(nextCategory?.id || CATEGORIES[0].id);
    }
    try {
      await dispatch({
        type: 'SET_CONFIG',
        playerId: currentUser.uid,
        totalRounds,
        selectedCategoryIds: selectedCategoryIds.slice(0, totalRounds),
      });
    } catch (err) {
      setError(humanizeFirebaseError(err));
    }
  };

  const handleCategoryChange = async (index: number, categoryId: string) => {
    if (!currentUser || !game) return;
    const selectedCategoryIds = [...game.selectedCategoryIds];
    selectedCategoryIds[index] = categoryId;
    try {
      await dispatch({
        type: 'SET_CONFIG',
        playerId: currentUser.uid,
        totalRounds: game.totalRounds,
        selectedCategoryIds,
      });
    } catch (err) {
      setError(humanizeFirebaseError(err));
    }
  };

  const handleShare = async () => {
    if (!gameId) return;
    try {
      setShareBusy(true);
      const result = await shareGame(gameId);
      if (result === 'copied') {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      setError(humanizeFirebaseError(err));
    } finally {
      setShareBusy(false);
    }
  };

  const handleStart = async () => {
    if (!currentUser) return;
    try {
      setStarting(true);
      await dispatch({ type: 'START', playerId: currentUser.uid });
    } catch (err) {
      setError(humanizeFirebaseError(err));
      setStarting(false);
    }
  };

  if (loading) {
    return <ScreenLoading play title="Loading the lobby" detail="Waiting for everyone to arrive." />;
  }

  if (!game) {
    return <ScreenError message={error || 'Game not found. Check the code and try again.'} onHome={() => navigate('/')} />;
  }

  const canStart = isHost && game.players.length >= 1 && game.selectedCategoryIds.length === game.totalRounds;

  return (
    <div className="ss-play">
      <header className="ss-topbar">
        <div className="ss-topbar-meta">
          <div className="ss-kicker">Lobby</div>
        </div>
        <MuteButton />
      </header>

      <div className="ss-sheet ss-lobby-panel">
        <p className="ss-kicker">Room code</p>
        <h1 className="ss-title">Lights up. Invite in.</h1>
        <p className="ss-lobby-code" aria-label={`Game code ${game.id}`}>
          {game.id}
        </p>
        <p className="ss-sub">Share this code or the invite link. Everyone needs to be signed in — then phones become the table.</p>

        {error && <div className="ss-error">{error}</div>}

        <button type="button" className="ss-btn ss-btn-ghost" onClick={handleShare} disabled={shareBusy} style={{ width: '100%' }}>
          {copied ? 'Copied' : shareBusy ? 'Sharing' : 'Share invite'}
        </button>

        <h2 className="ss-label" style={{ marginTop: '1.2rem' }}>
          Players ({game.players.length})
        </h2>
        {game.players.map((player) => (
          <div key={player.id} className="ss-player-chip">
            <span>{player.name}</span>
            {player.id === currentUser?.uid && <span className="ss-you">You</span>}
            {player.isHost && <span className="ss-pill">Host</span>}
          </div>
        ))}

        {isHost ? (
          <>
            <h2 className="ss-label" style={{ marginTop: '0.8rem' }}>
              Rounds
            </h2>
            <select
              className="ss-select"
              value={game.totalRounds}
              aria-label="Number of rounds"
              onChange={(event) => handleNumRoundsChange(Number(event.target.value))}
            >
              {Array.from({ length: MAX_ROUNDS - MIN_ROUNDS + 1 }, (_, index) => index + MIN_ROUNDS).map((num) => (
                <option key={num} value={num}>
                  {num} {num === 1 ? 'round' : 'rounds'}
                </option>
              ))}
            </select>
            {Array.from({ length: game.totalRounds }).map((_, index) => (
              <label key={index} className="ss-label">
                Round {index + 1} category
                <select
                  className="ss-select"
                  value={game.selectedCategoryIds[index] || ''}
                  onChange={(event) => handleCategoryChange(index, event.target.value)}
                >
                  <option value="">Select a category</option>
                  {CATEGORIES.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            <div className="ss-lobby-actions">
              <button
                type="button"
                className="ss-btn ss-btn-primary"
                style={{ width: '100%' }}
                onClick={handleStart}
                disabled={starting || !canStart}
              >
                {starting ? 'Starting' : game.players.length < 2 ? 'Start anyway' : 'Start game'}
              </button>
              <button type="button" className="ss-btn ss-btn-ghost" style={{ width: '100%' }} onClick={() => navigate('/')}>
                Home
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="ss-sub">Waiting for the host to start.</p>
            <button type="button" className="ss-btn ss-btn-ghost" style={{ width: '100%', marginTop: '0.6rem' }} onClick={() => navigate('/')}>
              Home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GameLobby;
